import React from 'react';

import type { ConciergeProductV2Detail } from '../../types';
import { trackRetailerProductPageOpen } from '../../tracking/trackRetailerProductPageOpen';
import { INK } from './constants';

/**
 * The product title in the detail panel.
 *
 * It is a link to the retailer's product page whenever one resolved. That link
 * used to live in the brand row as a "View" pill, but when this app has a bag
 * that pill is replaced by "Add to bag" (see `PriceBlock`) — and the shopper
 * still needs a way out to the listing. The title is the natural carrier: it
 * already names the thing being linked, and a link on a title reads as
 * "open it" everywhere else on the web.
 *
 * Deliberately NOT underlined and NOT recolored. The panel already has exactly
 * one filled action; a second styled-up control next to it competes with the
 * one we want clicked.
 */
export function PanelTitle({ title, detail }: { title: string; detail: ConciergeProductV2Detail | null }) {
  const style: React.CSSProperties = {
    margin: 0,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
    color: INK,
  };

  const url = detail?.retailerProductPageUrl;
  if (!detail || !url) return <p style={style}>{title}</p>;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      // Same event the "View" pill fired — the external-order pipeline keys on
      // this click, so moving the link must not lose it.
      onClick={() =>
        trackRetailerProductPageOpen({
          stacklineSku: detail.stacklineSku,
          retailerSku: detail.retailerSku,
          retailerId: detail.retailerId,
          retailerName: detail.retailerName,
          retailPrice: detail.retailPrice,
          retailerProductPageUrl: url,
          isAvailable: detail.isAvailable,
        })
      }
      style={{ ...style, display: 'block', textDecoration: 'none', cursor: 'pointer' }}
    >
      {title}
    </a>
  );
}
